import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {HttpResponse} from '@angular/common/http';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/filter';
import {User} from '../models/user.model';
import {UserService} from '../services/user.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ReportDatepickerComponent} from '../modals/report-datepicker.component';
import {ReportType} from '../models/report-type.model';
import {SidebarService} from '../services/side-bar.service';
import {AuthService} from '../services/auth.service';
import {NavigationEnd, Router} from '@angular/router';
import {NotificationsService} from '../services/notifications.service';
import {OperatingStandardService} from '../services/operating-standard.service';
import {StepsControllerService} from '../services/steps-controller.service';
import {SuggestionListsService} from '../content-page/common/suggestion-lists/services/suggestion-lists.service';
import {ApiConnectService} from '../services/api-connect.service';
import {TreeGroupPosService} from '../content-page/admin/create-operating-standard/tree-group-pos-service/tree-group-pos.service';
import {TaskBarService} from '../services/task-bar.service';
import {isStandardsPhone} from '../content-page/my-standards/standards-viewport';
import {
  DEAL_PAYROLL_LEGACY_ROUTE,
  GENERAL_SETTINGS_ROUTE,
  TMC_MENU_OPTION,
  ROUTE_PATH_INDEX,
  MENU_MISSING_INDEX,
  MENU_ROUTE_SEPARATOR,
  NAVBAR_ROLES,
  QUERY_MENU_OPTIONS,
  STANDARDS_MENU_OPTIONS,
  WORKFLOW_MENU_ROUTES
} from './navbar.constants';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css']
})
export class NavbarComponent implements OnInit, OnDestroy {
  private user: User;
  private destroy$ = new Subject<void>();
  isAdmin = false;
  isHead = false;
  isUser = false;
  isStorekeeper = false;
  specialRole = false;
  canShowSpecializations = false;
  id;

  get primaryDepartmentId(): string {
    return this.user ? this.user.mainGroup : null;
  }

  get isPhoneDepartmentNavigation(): boolean { return isStandardsPhone(); }

  constructor(private userService: UserService,
              private authService: AuthService,
              public sidebarService: SidebarService,
              private modalService: NgbModal,
              private router: Router,
              public apiConnectService: ApiConnectService,
              private taskbarService: TaskBarService,
              private treeGroupPosService: TreeGroupPosService,
              public stepsControllerService: StepsControllerService,
              public operatingStandardService: OperatingStandardService,
              private suggestionListsService: SuggestionListsService) {
              this.id = this.userService.getLocal().id;
  }

  @Output() featureSelected = new EventEmitter<string>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.user = this.userService.getLocal();
    this.isHead = this.user.roles.indexOf(NAVBAR_ROLES.head) !== MENU_MISSING_INDEX;
    this.isAdmin = this.user.roles.indexOf(NAVBAR_ROLES.admin) !== MENU_MISSING_INDEX;
    this.isUser = this.user.roles.indexOf(NAVBAR_ROLES.user) !== MENU_MISSING_INDEX;
    this.isStorekeeper = this.hasStorekeeperRole();
    this.specialRole = this.user.roles.indexOf(NAVBAR_ROLES.specializationAdmin) !== MENU_MISSING_INDEX;
    this.observeMenuNavigation();
    this.resolveSpecializationsMenuAccess();
  }

  private observeMenuNavigation(): void {
    if (!this.router || !this.router.events) { return; }
    this.selectMenuRoute(this.router.url);
    this.router.events.filter(event => event instanceof NavigationEnd).takeUntil(this.destroy$)
      .subscribe((event: NavigationEnd) => {
        this.selectMenuRoute(event.urlAfterRedirects || event.url);
      });
  }

  private selectMenuRoute(url: string): void {
    const path = url.split(/[?#]/)[ROUTE_PATH_INDEX];
    const workflow = WORKFLOW_MENU_ROUTES.filter(route =>
      path === route.path || path.indexOf(route.path + MENU_ROUTE_SEPARATOR) === ROUTE_PATH_INDEX)[ROUTE_PATH_INDEX];
    const option = workflow ? workflow.option : (path !== url ? QUERY_MENU_OPTIONS[path] : null);
    if (!option) { return; }
    this.sidebarService.setSelectedMenuOption(option);
    this.clearMenuGroups(option);
  }

  private clearMenuGroups(option: string): void {
    this.sidebarService.setSelectedGroupOptionControll(false);
    this.sidebarService.setSelectedGroupOptionStandards(false);
    this.sidebarService.setSelectedSL(false);
    this.sidebarService.setSelectedGroupOptionOpStandards(STANDARDS_MENU_OPTIONS.indexOf(option) !== MENU_MISSING_INDEX);
    this.sidebarService.setSelectedStandardsManagement(false);
  }

  goToCreateSt() {
    this.stepsControllerService.leaveComponent();
    this.treeGroupPosService.clearPositionsForServerLists();
    this.router.navigate(['/dashboard/create-operating-standard']);
  }

  openReportDatepicker() {
    const modalRef = this.modalService.open(ReportDatepickerComponent);
    modalRef.componentInstance.reportType = ReportType.BY_USER;
    modalRef.componentInstance.userId = this.userService.getLocal().id;
  }

  goToNewSL() {
    this.suggestionListsService.setListType('free');
    this.suggestionListsService.clearSLData();
    this.router.navigate(['/dashboard/create-suggestion-list']);
  }

  openStorekeeperEmployees(): void {
    this.sidebarService.setSelectedMenuOption('storekeeper-employees');
    this.closeSidebarOnMobile();
    this.router.navigate(['/dashboard/company-structure-user'], {
      queryParams: { tab: 'employees' }
    });
  }

  openDepartments(): void {
    if (this.isStorekeeper && this.isMobileView()) {
      this.openStorekeeperEmployees();
      return;
    }

    this.sidebarService.setSelectedMenuOption('m-groups');
    this.closeSidebarOnMobile();
    this.router.navigate(['/dashboard/company-structure-user']);
  }

  openCalendar(): void {
    this.sidebarService.setSelectedMenuOption('learnit');
    this.sidebarService.setSelectedGroupOptionControll(false);
    this.sidebarService.setSelectedGroupOptionStandards(false);
    this.sidebarService.setSelectedSL(false);
    this.sidebarService.setSelectedGroupOptionOpStandards(false);
    this.taskbarService.closeTaskBar();
    this.closeSidebarOnMobile();
    this.router.navigate(['/dashboard/calendar']);
  }

  isGeneralSettingsRoute(): boolean {
    if (!this.router) {
      return false;
    }
    const routePath = this.router.url.split(/[?#]/)[ROUTE_PATH_INDEX];
    return routePath === GENERAL_SETTINGS_ROUTE
      || routePath === DEAL_PAYROLL_LEGACY_ROUTE;
  }

  isAdminSettingsRoute(): boolean {
    return this.isGeneralSettingsRoute() || this.sidebarService.selectedMenuOption === TMC_MENU_OPTION;
  }

  private hasStorekeeperRole(): boolean {
    if (!this.user || !this.user.roles) {
      return false;
    }
    if (this.user.roles.indexOf('ROLE_STOREKEEPER') !== -1) {
      return true;
    }
    return this.user.roles.indexOf('STOREKEEPER') !== -1;
  }

  private resolveSpecializationsMenuAccess(): void {
    this.canShowSpecializations = this.isAdmin || this.isHead || this.specialRole;
    if (this.canShowSpecializations) {
      return;
    }
    this.userService.getManagedSkillGroups().takeUntil(this.destroy$).subscribe(
      (response: HttpResponse<Object[]>) => {
        const groups = response && response.body ? response.body : [];
        if (groups.length) {
          this.canShowSpecializations = true;
          return;
        }
        this.resolveAssignedSpecializationsMenuAccess();
      },
      () => this.resolveAssignedSpecializationsMenuAccess()
    );
  }

  private resolveAssignedSpecializationsMenuAccess(): void {
    this.userService.getSkillSpecializations().takeUntil(this.destroy$).subscribe(
      (response: HttpResponse<Object[]>) => {
        const specializations = response && response.body ? response.body : [];
        this.canShowSpecializations = !!specializations.length;
      },
      () => this.canShowSpecializations = false
    );
  }

  closeSidebarOnMobile(): void {
    if (window.innerWidth > 1023) {
      return;
    }
    this.sidebarService.setSideBar(false);
  }

  private isMobileView(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 1023;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/navbar/navbar.component.ts